<template>
  <div
    ref="container"
    :class="$style.container"
  >
    <slot name="target" />
    <transition
      :name="$style.slideTransition"
      @after-enter="handleTransitionEnd"
    >
      <div
        v-if="dropDownVisible"
        v-on-clickaway="hideList"
        :class="[$style.dropDownContainer, {
          [$style.dropDownContainerAlignRight]: dropDownAlignRight,
        }]"
        :style="{
          ...(dropDownMaxHeight && { maxHeight: dropDownMaxHeight }),
          ...(dropDownMaxWidth && { maxWidth: dropDownMaxWidth }),
        }"
      >
        <slot name="dropDown" />
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * This component handles the display and positioning of a dropdown element.
 * You may use v-model on this component to read the visibility of the dropdown.
 */
import { mixin as clickaway } from 'vue-clickaway';

export default {
  name: 'DropDownContainer',

  mixins: [clickaway],

  model: {
    prop: 'dropDownVisible',
    event: 'toggleDropdownVisibility',
  },

  props: {
    // This value is needed to provide an opening/closing transition for the dropdown
    dropDownMaxHeight: {
      type: String,
      default: '320px',
    },
    // This value helps determine when the dropdown will hit the edge and need to be right-aligned
    dropDownMaxWidth: {
      type: [String, Number],
      default: '',
    },
    dropDownVisible: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    dropDownAlignRight: false,
    openingList: false,
  }),

  watch: {
    dropDownVisible(newValue) {
      if (newValue === true) {
        this.setListPosition();
        this.openingList = true;
        this.$emit('open');
      }
      if (newValue === false) {
        this.$emit('close');
      }
    },
  },

  methods: {
    handleTransitionEnd() {
      this.openingList = false;
    },

    hideList() {
      if (!this.openingList) {
        this.$emit('toggleDropdownVisibility', false);
      }
    },

    /**
     * This method calculates whether the dropdown will display past the right edge of the screen,
     * thus needing to be right-aligned. Since the width of the dropdown content is not known at
     * this time (not yet opened), we are relying on the dropDownMaxWidth prop.
     */
    setListPosition() {
      // This won't work with % values
      if (typeof this.dropDownMaxWidth === 'string' && this.dropDownMaxWidth.includes('%')) return;

      const dropDownMaxWidth = parseFloat(this.dropDownMaxWidth);
      if (Number.isNaN(dropDownMaxWidth)) return;

      const windowWidth = window.innerWidth;
      const { container } = this.$refs;
      const containerPosition = container.getBoundingClientRect();

      if (containerPosition.left + dropDownMaxWidth > windowWidth) {
        this.dropDownAlignRight = true;
      } else {
        this.dropDownAlignRight = false;
      }
    },
  },
};
</script>

<style module>
@value (dropDownMenuZIndex) from '~css/z-index.css';

.container { position: relative; }

.dropDownContainer {
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: dropDownMenuZIndex;
}

.dropDownContainerAlignRight { right: 0; }
.dropDownContainer > * { min-height: 0; }

.slideTransition,
.slideTransition-enter-active,
.slideTransition-leave-active {
  overflow: hidden;
  transition: max-height 0.2s ease-in-out;
}

.slideTransition-enter,
.slideTransition-leave-to {
  max-height: 0 !important; /* This is to override the inline max-height style */
  overflow: hidden;
}
</style>
